import './ShipmentShowPackages.scss';

import { CloseOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Flex, InputNumber, message } from 'antd';
import SpaceAddon from 'antd/es/space/Addon';
import Compact from 'antd/es/space/Compact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PackageDimensions } from '@/model/Logistic/Package';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Shipment } from '@/model/Logistic/Shipment/Shipment';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface PackageDetailLine {
  id: number;
  height?: number | null;
  width?: number | null;
  length?: number | null;
  weight?: number | null;
}

const normalizePackageDimensions = (
  lines: PackageDetailLine[] | PackageDimensions[] = [],
): PackageDimensions[] =>
  lines.map(line => ({
    height: line.height ?? null,
    width: line.width ?? null,
    length: line.length ?? null,
    weight: line.weight ?? null,
  }));
const hasCompletePackageDimensions = (line: PackageDetailLine) =>
  line.height !== undefined &&
  line.width !== undefined &&
  line.length !== undefined &&
  line.weight !== undefined;

interface ShipmentShowPackagesProps {
  record?: Shipment;
  refetch?: () => void;
  setBlockChangeState?: (block: { id: string; message: string }[]) => void;
}

/**
 * Componente solo utilizado para Brasil
 */
const ShipmentShowPackages = (props: ShipmentShowPackagesProps) => {
  const { userWorkArea } = useUserContext();
  const { t } = useTranslation();
  const { shipmentRepository } = useRepositoriesContext();
  const { record, refetch, setBlockChangeState } = props;
  const [packageDetailLines, setPackageDetailLines] = useState<PackageDetailLine[]>([]);
  const packageDetailLineId = useRef(0);
  const packageDimensions = useMemo(
    () => normalizePackageDimensions(packageDetailLines),
    [packageDetailLines],
  );
  const savedPackageDimensions = useMemo(
    () => normalizePackageDimensions(record?.packages_dimensions),
    [record?.packages_dimensions],
  );
  // Solo se recalcula cuando cambian las dimensiones editadas o las guardadas.
  const hasPackageDimensionChanges = useMemo(
    () => JSON.stringify(packageDimensions) !== JSON.stringify(savedPackageDimensions),
    [packageDimensions, savedPackageDimensions],
  );
  const visiblePackagesCount = record?.packages_count ?? packageDetailLines.length;

  useEffect(() => {
    const lines =
      record?.packages_dimensions?.map((dimensions, index) => ({
        id: index + 1,
        ...dimensions,
      })) ?? [];

    packageDetailLineId.current = lines.length;
    setPackageDetailLines(lines);
  }, [record?.id, record?.packages_dimensions]);

  const updatePackageDetailLine = useCallback((id: number, values: Partial<PackageDetailLine>) => {
    setPackageDetailLines(lines =>
      lines.map(line => (line.id === id ? { ...line, ...values } : line)),
    );
  }, []);
  const removePackageDetailLine = useCallback((id: number) => {
    setPackageDetailLines(lines => lines.filter(line => line.id !== id));
  }, []);
  const addPackageDetailLine = useCallback(() => {
    packageDetailLineId.current += 1;
    setPackageDetailLines(lines => [...lines, { id: packageDetailLineId.current }]);
    setBlockChangeState?.([
      { id: 'package', message: t('Se han realizado cambios en los paquetes') },
    ]);
  }, [setBlockChangeState, t]);
  const clearPackageDetailChanges = useCallback(() => {
    setPackageDetailLines(
      record?.packages_dimensions?.map((dimensions, index) => ({
        id: index + 1,
        ...dimensions,
      })) ?? [],
    );
    setBlockChangeState?.([]);
  }, [record?.packages_dimensions, setBlockChangeState]);
  const savePackageDimensions = useCallback(async () => {
    if (!record?.id) return;

    if (packageDetailLines.some(line => !hasCompletePackageDimensions(line))) {
      message.error(t('Completá alto, ancho, largo y peso de cada paquete'));

      return;
    }

    // Metadata: solo se envia packages_dimensions; packages_count lo recalcula backend.
    await shipmentRepository.updateShipment(record.id, {
      packages_dimensions: packageDimensions,
    });
    setBlockChangeState?.([]);
    refetch?.();
  }, [
    packageDetailLines,
    packageDimensions,
    record?.id,
    refetch,
    setBlockChangeState,
    shipmentRepository,
    t,
  ]);
  // Solo se puede editar en estado en preparación
  const canEditPackageInfo =
    userWorkArea === USER_WORK_AREA.LOGISTICS && record?.status == PACKAGE_STATES.PREPARACION;

  return (
    <div className="ShipmentPackageDetailsEditor">
      <Flex justify="space-between" align="center" gap={8} className="ShipmentPackageHeader">
        {packageDetailLines.length === 0 ? (
          <div className="ShipmentPackageEmptyState">{t('Todavía no se agregaron paquetes')}</div>
        ) : (
          <b>
            {t('Paquetes agregados')}: {visiblePackagesCount}
          </b>
        )}

        {canEditPackageInfo && (
          <Button size="small" icon={<PlusOutlined />} onClick={addPackageDetailLine}>
            {t('Agregar paquete')}
          </Button>
        )}
      </Flex>

      {packageDetailLines.length > 0 ? (
        <div className="ShipmentPackageRows">
          {packageDetailLines.map((line, index) => (
            <div className="ShipmentPackageRow" key={line.id}>
              <b>
                {t('Paquete')} {index + 1}
              </b>
              <Compact>
                <InputNumber
                  value={line.height}
                  min={0}
                  placeholder={t('Alto')}
                  disabled={!canEditPackageInfo}
                  style={{ width: '100%' }}
                  onChange={height => updatePackageDetailLine(line.id, { height })}
                />
                <SpaceAddon>{'cm'}</SpaceAddon>
              </Compact>

              <Compact>
                <InputNumber
                  value={line.width}
                  min={0}
                  placeholder={t('Ancho')}
                  disabled={!canEditPackageInfo}
                  style={{ width: '100%' }}
                  onChange={width => updatePackageDetailLine(line.id, { width })}
                />
                <SpaceAddon>{'cm'}</SpaceAddon>
              </Compact>
              <Compact>
                <InputNumber
                  value={line.length}
                  min={0}
                  placeholder={t('Largo')}
                  disabled={!canEditPackageInfo}
                  style={{ width: '100%' }}
                  onChange={length => updatePackageDetailLine(line.id, { length })}
                />
                <SpaceAddon>{'cm'}</SpaceAddon>
              </Compact>
              <Compact>
                <InputNumber
                  value={line.weight}
                  min={0}
                  placeholder={t('Peso')}
                  disabled={!canEditPackageInfo}
                  style={{ width: '100%' }}
                  onChange={weight => updatePackageDetailLine(line.id, { weight })}
                />
                <SpaceAddon>{'kg'}</SpaceAddon>
              </Compact>
              {canEditPackageInfo && (
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removePackageDetailLine(line.id)}
                />
              )}
            </div>
          ))}
        </div>
      ) : null}
      <Flex gap={8} wrap justify="end">
        {hasPackageDimensionChanges && (
          <Button size="small" icon={<CloseOutlined />} onClick={clearPackageDetailChanges}>
            {t('Cancelar')}
          </Button>
        )}
        {canEditPackageInfo && hasPackageDimensionChanges && (
          <Button
            size="small"
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => void savePackageDimensions()}>
            {t('Guardar')}
          </Button>
        )}
      </Flex>
    </div>
  );
};

export default ShipmentShowPackages;
